import React, { lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
} from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { MarketplaceProvider } from "./context/MarketplaceContext";
import PrivateRoute from "./components/PrivateRoute";
import AdminRoute from "./components/AdminRoute";
import Layout from "./components/layout/Layout";
import AdminLayout from "./components/layout/AdminLayout";
import LoadingSpinner from "./components/common/LoadingSpinner";
import AdminOrdersPage from "./pages/admin/AdminOrdersPage";
import GoogleTagManager from "./components/analytics/GoogleTagManager";

// Lazy-loaded page components for better performance
const HomePage = lazy(() => import("./pages/HomePage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const ProductListPage = lazy(() => import("./pages/ProductListPage"));
const ProductDetailPage = lazy(() => import("./pages/ProductDetailPage"));
const CartPage = lazy(() => import("./pages/CartPage"));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const OrderConfirmationPage = lazy(
  () => import("./pages/OrderConfirmationPage")
);
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const OrderDetailPage = lazy(() => import("./pages/OrderDetailPage"));
const ContentPage = lazy(() => import("./pages/ContentPage"));
const BlogListPage = lazy(() => import("./pages/BlogListPage"));
const BlogDetailPage = lazy(() => import("./pages/BlogDetailPage"));
const ProjectListPage = lazy(() => import("./pages/ProjectListPage"));
const ProjectInfo = lazy(() => import("./pages/ProjectInfo"));
const ProjectDetailPage = lazy(() => import("./pages/ProjectDetailPage"));
const RequestServicePage = lazy(() => import("./pages/RequestServicePage"));
const ChatbotPage = lazy(() => import("./pages/ChatbotPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const ContentSearchResultsPage = lazy(
  () => import("./pages/ContentSearchResultsPage")
);
const FullStackTrainingPage = lazy(
  () => import("./pages/FullStackTrainingPage")
);
const CategoryDetailPage = lazy(() => import("./pages/CategoryDetailPage"));
const CategoryCardsPage = lazy(() => import("./pages/CategoryCardsPage"));

// Admin pages with separate chunk
const AdminDashboardPage = lazy(
  () => import("./pages/admin/AdminDashboardPage")
);
const AdminUserListPage = lazy(() => import("./pages/admin/AdminUserListPage"));
const AdminUserDetailPage = lazy(
  () => import("./pages/admin/AdminUserDetailPage")
);
const AdminProductListPage = lazy(
  () => import("./pages/admin/AdminProductListPage")
);
const AdminProductFormPage = lazy(
  () => import("./pages/admin/AdminProductFormPage")
);
const AdminCategoryListPage = lazy(
  () => import("./pages/admin/AdminCategoryListPage")
);
const AdminCategoryCreatePage = lazy(
  () => import("./pages/admin/AdminCategoryCreatePage")
);
const AdminCategoryEditPage = lazy(
  () => import("./pages/admin/AdminCategoryEditPage")
);
const AdminOrderListPage = lazy(
  () => import("./pages/admin/AdminOrderListPage")
);
const AdminOrderDetailPage = lazy(
  () => import("./pages/admin/AdminOrderDetailPage")
);
const AdminContentListPage = lazy(
  () => import("./pages/admin/AdminContentListPage")
);
const AdminContentCreatePage = lazy(
  () => import("./pages/admin/AdminContentCreatePage")
);
const AdminContentEditPage = lazy(
  () => import("./pages/admin/AdminContentEditPage")
);
const AdminHomepageSectionsPage = lazy(
  () => import("./pages/admin/AdminHomepageSectionsPage")
);
const AdminBlogManagementPage = lazy(
  () => import("./pages/admin/AdminBlogManagementPage")
);
const AdminBlogPostFormPage = lazy(
  () => import("./pages/admin/AdminBlogPostFormPage")
);
const AdminProjectListPage = lazy(
  () => import("./pages/admin/AdminProjectListPage")
);
const AdminProjectFormPage = lazy(
  () => import("./pages/admin/AdminProjectFormPage")
);
const AdminServiceRequestsPage = lazy(
  () => import("./pages/admin/AdminServiceRequestsPage")
);

// Add imports for the new components
const AdminCreateHomepageSectionPage = lazy(
  () => import("./pages/admin/AdminCreateHomepageSectionPage")
);
const AdminEditHomepageSectionPage = lazy(
  () => import("./pages/admin/AdminEditHomepageSectionPage")
);

// Wrapper component to provide children to Layout
const LayoutWrapper = () => (
  <Layout>
    <Outlet />
  </Layout>
);

function App() {
  return (
    <Router>
      <GoogleTagManager />
      <AuthProvider>
        <MarketplaceProvider>
          <Suspense
            fallback={
              <div className="w-full h-screen flex items-center justify-center">
                <LoadingSpinner size="large" />
              </div>
            }
          >
            <Routes>
              <Route element={<LayoutWrapper />}>
                {/* Public Routes */}
                <Route path="/" element={<HomePage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/products" element={<ProductListPage />} />
                <Route path="/products/:slug" element={<ProductDetailPage />} />
                <Route path="/cart" element={<CartPage />} />
                <Route path="/blog" element={<BlogListPage />} />
                <Route path="/blog/:slug" element={<BlogDetailPage />} />
                <Route path="/projects" element={<ProjectListPage />} />
                <Route path="/projects/:slug" element={<ProjectDetailPage />} />
                <Route path="/projectInfo" element={<ProjectInfo />} />
                <Route
                  path="/request-service"
                  element={<RequestServicePage />}
                />
                <Route path="/chatbot" element={<ChatbotPage />} />
                <Route
                  path="/trainings/full-stack"
                  element={<FullStackTrainingPage />}
                />
                <Route
                  path="/services"
                  element={<CategoryCardsPage category="services" />}
                />
                <Route
                  path="/resources"
                  element={<CategoryCardsPage category="resources" />}
                />
                <Route
                  path="/marketplace"
                  element={<CategoryCardsPage category="marketplace" />}
                />
                <Route
                  path="/trainings/:slug"
                  element={<CategoryDetailPage category="trainings" />}
                />
                <Route
                  path="/content-search-results"
                  element={<ContentSearchResultsPage />}
                />

                {/* Dedicated routes for specific content pages */}
                <Route path="/help" element={<ContentPage />} />
                <Route path="/about" element={<ContentPage />} />
                <Route path="/terms" element={<ContentPage />} />
                <Route path="/privacy" element={<ContentPage />} />

                {/* Protected Routes */}
                <Route element={<PrivateRoute />}>
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/checkout" element={<CheckoutPage />} />
                  <Route
                    path="/order-confirmation/:orderId"
                    element={<OrderConfirmationPage />}
                  />
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route
                    path="/orders/:orderId"
                    element={<OrderDetailPage />}
                  />
                </Route>

                {/* Content Pages Catch-all Route - MUST BE LAST to avoid intercepting other routes */}
                <Route path="/:slug" element={<ContentPage />} />
              </Route>

              {/* Admin Routes with AdminLayout */}
              <Route element={<AdminRoute />}>
                <Route element={<AdminLayout />}>
                  <Route path="/admin" element={<AdminDashboardPage />} />
                  <Route path="/admin/users" element={<AdminUserListPage />} />
                  <Route
                    path="/admin/users/:userId"
                    element={<AdminUserDetailPage />}
                  />
                  <Route
                    path="/admin/products"
                    element={<AdminProductListPage />}
                  />
                  <Route
                    path="/admin/products/create"
                    element={<AdminProductFormPage />}
                  />
                  <Route
                    path="/admin/products/:productId/edit"
                    element={<AdminProductFormPage />}
                  />
                  <Route
                    path="/admin/categories"
                    element={<AdminCategoryListPage />}
                  />
                  <Route
                    path="/admin/categories/new"
                    element={<AdminCategoryCreatePage />}
                  />
                  <Route
                    path="/admin/categories/:id/edit"
                    element={<AdminCategoryEditPage />}
                  />
                  <Route path="/admin/orders" element={<AdminOrdersPage />} />
                  <Route
                    path="/admin/orders/:orderId"
                    element={<AdminOrderDetailPage />}
                  />
                  <Route
                    path="/admin/content"
                    element={<AdminContentListPage />}
                  />
                  <Route
                    path="/admin/content/create"
                    element={<AdminContentCreatePage />}
                  />
                  <Route
                    path="/admin/content/:contentId/edit"
                    element={<AdminContentEditPage />}
                  />
                  <Route
                    path="/admin/homepage-sections"
                    element={<AdminHomepageSectionsPage />}
                  />
                  <Route
                    path="/admin/homepage-sections/new"
                    element={<AdminCreateHomepageSectionPage />}
                  />
                  <Route
                    path="/admin/homepage-sections/edit/:sectionId"
                    element={<AdminEditHomepageSectionPage />}
                  />
                  <Route
                    path="/admin/blog"
                    element={<AdminBlogManagementPage />}
                  />
                  <Route
                    path="/admin/blog/create"
                    element={<AdminBlogPostFormPage />}
                  />
                  <Route
                    path="/admin/blog/edit/:id"
                    element={<AdminBlogPostFormPage />}
                  />
                  <Route
                    path="/admin/projects"
                    element={<AdminProjectListPage />}
                  />
                  <Route
                    path="/admin/projects/create"
                    element={<AdminProjectFormPage />}
                  />
                  <Route
                    path="/admin/projects/edit/:id"
                    element={<AdminProjectFormPage />}
                  />
                  <Route
                    path="/admin/projects/view/:id"
                    element={<AdminProjectFormPage />}
                  />
                  <Route
                    path="/admin/service-requests"
                    element={<AdminServiceRequestsPage />}
                  />
                </Route>
              </Route>

              {/* 404 Route */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </MarketplaceProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
